"use client";

import React from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { LocalizedLink } from '@/components/LocalizedLink';
import { useLocale } from '@/contexts/LocaleContext';
import { getPathForLocale } from '@/lib/i18n';
import { getApiBaseUrl } from '@/lib/api';
import { loadRecaptcha, executeRecaptcha, unloadRecaptcha } from '@/lib/recaptcha';
import { GoogleSignInButton } from '@/components/GoogleSignInButton';
import { useTranslations } from '@/hooks/useTranslations';

const inputClassName =
  'min-h-12 w-full rounded-2xl border border-white/10 bg-white/5 px-4 text-base text-slate-100 placeholder:text-slate-500 transition focus:border-[#ffb74d] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#ffb74d]/45';
const primaryButtonClassName =
  'inline-flex min-h-12 w-full items-center justify-center rounded-2xl bg-[#ffb74d] px-5 py-3 text-sm font-bold text-[#3a2100] shadow-lg shadow-[#ffb74d]/20 transition hover:bg-[#ffd08a] focus:outline-none focus:ring-2 focus:ring-[#ffb74d] focus:ring-offset-2 focus:ring-offset-[#15192b] disabled:cursor-not-allowed disabled:opacity-60';

export function RegisterForm() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const locale = useLocale();
  const { t } = useTranslations();
  const displayNameRef = React.useRef<HTMLInputElement | null>(null);
  const emailRef = React.useRef<HTMLInputElement | null>(null);
  const passwordRef = React.useRef<HTMLInputElement | null>(null);
  const [message, setMessage] = React.useState<string | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [showPassword, setShowPassword] = React.useState(false);
  const [acceptTerms, setAcceptTerms] = React.useState(false);
  const [registered, setRegistered] = React.useState(false);

  React.useEffect(() => {
    loadRecaptcha().catch(() => {
      // Recaptcha loading failed, but we continue without it
    });
    return () => unloadRecaptcha();
  }, []);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setMessage(null);

    try {
      const displayName = displayNameRef.current?.value?.trim() ?? '';
      const email = emailRef.current?.value?.trim() ?? '';
      const password = passwordRef.current?.value ?? '';
      const captchaToken = await executeRecaptcha('register');
      
      const response = await fetch(`${getApiBaseUrl()}/auth/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ displayName, email, password, captchaToken }),
      });
      if (!response.ok) {
        let errorMessage = 'Registration failed.';
        const raw = await response.text();
        try {
          const data = JSON.parse(raw) as { message?: string };
          if (data.message) errorMessage = data.message;
        } catch {
          if (raw) errorMessage = raw;
        }
        throw new Error(errorMessage);
      }
      const data = (await response.json()) as { message?: string };
      setRegistered(true);
      setMessage(data.message ?? 'Check your email to verify your account.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  if (registered) {
    return (
      <div className="space-y-4" role="status" aria-live="polite">
        <p className="rounded-2xl border border-emerald-400/20 bg-emerald-400/10 px-4 py-3 text-sm text-emerald-100">
          {message}
        </p>
        <p className="text-sm leading-6 text-slate-300">
          {t('authFlow.register.successText')}
        </p>
        <p>
          <LocalizedLink
            href="/login"
            className="inline-flex min-h-11 items-center justify-center rounded-2xl border border-[#ffb74d]/30 bg-[#ffb74d]/10 px-4 py-2 text-sm font-bold text-[#ffd08a] transition hover:bg-[#ffb74d]/15 focus:outline-none focus:ring-2 focus:ring-[#ffb74d]/60"
          >
            {t('auth.signIn')}
          </LocalizedLink>
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} method="post" className="space-y-5">
      <div>
        <label htmlFor="register-name" className="mb-2 block text-sm font-semibold text-slate-200">
          {t('authFlow.fields.displayName')}
        </label>
        <input
          ref={displayNameRef}
          id="register-name"
          className={inputClassName}
          placeholder={t('authFlow.fields.displayNamePlaceholder')}
          required
          name="name"
          autoComplete="name"
        />
      </div>
      <div>
        <label htmlFor="register-email" className="mb-2 block text-sm font-semibold text-slate-200">
          {t('authFlow.fields.email')}
        </label>
        <input
          ref={emailRef}
          id="register-email"
          className={inputClassName}
          placeholder={t('authFlow.fields.emailPlaceholder')}
          required
          type="email"
          name="email"
          autoComplete="email"
        />
      </div>
      <div>
        <label htmlFor="register-password" className="mb-2 block text-sm font-semibold text-slate-200">
          {t('authFlow.fields.password')}
        </label>
        <div className="relative">
          <input
            ref={passwordRef}
            id="register-password"
            className={`${inputClassName} pr-12`}
            placeholder={t('authFlow.fields.newPasswordPlaceholder')}
            required
            type={showPassword ? 'text' : 'password'}
            minLength={6}
            name="password"
            autoComplete="new-password"
          />
          <button
            type="button"
            aria-label={showPassword ? t('authFlow.fields.hidePassword') : t('authFlow.fields.showPassword')}
            className="absolute right-2 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full text-slate-300 transition hover:bg-white/10 hover:text-white focus:outline-none focus:ring-2 focus:ring-[#ffb74d]/60"
            onClick={() => setShowPassword((value) => !value)}
          >
            <svg
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="h-5 w-5"
              aria-hidden
            >
              <path d="M2.5 12s3.5-7 9.5-7 9.5 7 9.5 7-3.5 7-9.5 7-9.5-7-9.5-7Z" />
              <circle cx="12" cy="12" r="3" />
            </svg>
          </button>
        </div>
      </div>
      <label className="flex cursor-pointer items-start gap-3 rounded-2xl border border-white/10 bg-white/[0.03] p-4 text-sm leading-6 text-slate-300 transition hover:border-white/20">
        <input
          type="checkbox"
          checked={acceptTerms}
          onChange={(e) => setAcceptTerms(e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-white/20 bg-white/5 text-[#ffb74d] focus:ring-[#ffb74d]/60"
        />
        <span>
          {t('authFlow.fields.agreePrefix')}{' '}
          <LocalizedLink
            href="/terms"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold text-[#ffb74d] underline underline-offset-4 transition hover:text-[#ffd08a] focus:outline-none focus:ring-2 focus:ring-[#ffb74d]/60"
          >
            {t('auth.termsAndConditions')}
          </LocalizedLink>
        </span>
      </label>
      <button
        type="submit"
        className={primaryButtonClassName}
        disabled={loading || !acceptTerms}
      >
        {loading ? t('register.creatingAccount') : t('register.createAccount')}
      </button>
      <div className="relative py-2">
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-[#15192b] px-3 text-xs font-medium uppercase tracking-[0.18em] text-slate-500">
          {t('authFlow.or')}
        </span>
        <div className="h-px bg-white/10" />
      </div>
      <GoogleSignInButton
        onSuccess={() => {
          const next = String(searchParams.get('next') ?? '').trim();
          const target = getPathForLocale(locale, next || '/account');
          router.replace(target);
          router.refresh();
        }}
        disabled={loading}
      />
      {message && (
        <p className="rounded-2xl border border-rose-400/20 bg-rose-400/10 px-4 py-3 text-sm text-rose-100" role="alert">
          {message}
        </p>
      )}
      <p className="text-center text-sm text-slate-400">
        {t('authFlow.register.alreadyHaveAccount')}{' '}
        <LocalizedLink
          href="/login"
          className="font-semibold text-[#ffb74d] underline underline-offset-4 transition hover:text-[#ffd08a] focus:outline-none focus:ring-2 focus:ring-[#ffb74d]/60"
        >
          {t('auth.signIn')}
        </LocalizedLink>
      </p>
    </form>
  );
}
